import { useCallback, useEffect, useState } from 'react';

import { ApiError, type Inbox, api } from '../lib/api';

const POLL_INTERVAL_MS = 10_000;

export { POLL_INTERVAL_MS as INBOX_POLL_INTERVAL_MS };

export interface InboxesState {
  readonly inboxes: readonly Inbox[];
  readonly loading: boolean;
  readonly error: string | null;
  readonly refresh: () => void;
  readonly create: (name: string) => Promise<Inbox | null>;
  readonly remove: (address: string) => Promise<void>;
}

/**
 * Loads and mutates the inbox list.
 *
 * `onUnauthorized` is passed in rather than the hook importing the session,
 * so that the one reaction to an expired token — drop it, show the login —
 * lives in App and every hook routes to it instead of each deciding for itself.
 */
export function useInboxes(token: string | null, onUnauthorized: () => void): InboxesState {
  const [inboxes, setInboxes] = useState<readonly Inbox[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback((): void => {
    if (!token) return;

    void api
      .listInboxes(token)
      .then((next) => {
        setInboxes(next);
        setError(null);
      })
      .catch((cause: unknown) => {
        if (cause instanceof ApiError && cause.unauthorized) {
          onUnauthorized();
          return;
        }

        // A failed poll leaves the last good list on screen. Blanking it would
        // turn one dropped request into an empty app.
        setError(cause instanceof ApiError ? cause.message : 'Could not reach the server.');
      })
      .finally(() => setLoading(false));
  }, [token, onUnauthorized]);

  useEffect(() => {
    setLoading(true);
    refresh();
  }, [refresh]);

  const create = useCallback(
    async (name: string): Promise<Inbox | null> => {
      if (!token) return null;

      try {
        const created = await api.createInbox(token, name);
        setInboxes((current) => [...current, created]);
        setError(null);

        return created;
      } catch (cause) {
        if (cause instanceof ApiError && cause.unauthorized) {
          onUnauthorized();
          return null;
        }

        setError(cause instanceof ApiError ? cause.message : 'Could not create that address.');
        return null;
      }
    },
    [token, onUnauthorized],
  );

  const remove = useCallback(
    async (address: string): Promise<void> => {
      if (!token) return;

      // Removed from the list first: the request is idempotent on the server,
      // so the worst case of an optimistic update here is that the next poll
      // puts the row back.
      setInboxes((current) => current.filter((inbox) => inbox.address !== address));

      try {
        await api.deleteInbox(token, address);
      } catch (cause) {
        if (cause instanceof ApiError && cause.unauthorized) onUnauthorized();
        else setError('Could not delete that address.');

        refresh();
      }
    },
    [token, onUnauthorized, refresh],
  );

  return { inboxes, loading, error, refresh, create, remove };
}
